//  React
import { ReactElement, useState } from 'react';
import { createPortal } from 'react-dom';
//  Modules
import classNames from 'classnames/bind';
// Design System
import { Slides, SlidesItem, SlideProps } from '@utmb/design-system/slides/Slides';
// Types
import { Slice } from '@utmb/types/slice-handler';
import { ExpandableCardsCollectionVariantEnum } from '@utmb/types/enum/ExpandableCardsCollectionVariantEnum';
// Styles
import styles from './expandable-cards-collection.module.scss';
import { ExpandableSlideContent } from './ExpandableSlideContent';
import { ExpandableSlidePhoto } from './ExpandableSlidePhoto';
import { ExpandableCardTitleOverPicture } from './expandable-card-title-over-picture/expandable-card-title-over-picture';
import SliceExpandableCardsCollectionView from '@utmb/types/view/SliceExpandableCardsCollectionView';
import SliceExpandableCardsCollectionCardView from '@utmb/types/view/SliceExpandableCardsCollectionCardView';
import ExpandableCardsCollectionModal from './ExpandableCardsCollectionModal';

const cx = classNames.bind(styles);

export const ExpandableCardsCollection = (props: Slice<SliceExpandableCardsCollectionView>): ReactElement | null => {
    const { id, isDark, expandableCards, expandableCardsCollectionVariant, disableSlide } = props;

    const [slideIndexModalContent, setSlideIndexModalContent] = useState<number | null>(null);

    if (!expandableCards || !expandableCards.length) return null;

    const isTitleOverPicture = expandableCardsCollectionVariant === ExpandableCardsCollectionVariantEnum.TITLE_OVER_PICTURE;

    const getVisibleDots = (key: number): string => {
        if (key === 2) {
            return styles.hide_dots_md;
        } else if (key === 3) {
            return styles.hide_dots_xl;
        } else if (key <= 1) {
            return styles.hide_all_bp;
        } else return '';
    };

    const rootCSS = ['container', styles.root].filter(Boolean).join(' ');
    const showSliderDotsCSS = [styles.dots, getVisibleDots(expandableCards.length)].join(' ');

    const slideProps: SlideProps = {
        className: cx({
            slide_picture_bottom_container: !isTitleOverPicture,
            mg_slide_container: isTitleOverPicture,
        }),
        carouselOptions: {
            containScroll: 'trimSnaps',
            align: 'center',
            draggable: true,
            loop: false,
        },
        dotsClassName: showSliderDotsCSS,
    };

    const slideContent = (card: SliceExpandableCardsCollectionCardView): ReactElement => {
        if (isTitleOverPicture) {
            return <ExpandableCardTitleOverPicture {...card} />;
        }

        return (
            <>
                <ExpandableSlideContent {...card} />
                <ExpandableSlidePhoto {...card} />
            </>
        );
    };

    const handleOpenModel = (index: number | null): void => {
        setSlideIndexModalContent(index);
    };

    // Because of the custom slide item width (in px/rem), we need to calculate the parent container width to center the slides
    // To be effective, this line has to be combined with slider option `align: true`
    const containerWidth = expandableCards.length * parseInt(styles.slideItemMaxWidth) + (expandableCards.length - 1) * parseInt(styles.slideItemMarginRight);

    return (
        <div id={id} className={rootCSS}>
            {disableSlide ? (
                <div className={styles.unslideContainer}>
                    {expandableCards.map((card: SliceExpandableCardsCollectionCardView, index: number) => (
                        <div
                            className={styles.unslideItem}
                            key={index}
                            onClick={() => {
                                handleOpenModel(index);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleOpenModel(index);
                                }
                            }}
                            role="button"
                            tabIndex={0}
                        >
                            {slideContent(card)}
                        </div>
                    ))}
                </div>
            ) : (
                <Slides {...slideProps} pagination={false} isDarkPagination={isDark} containerWidth={containerWidth} dots={true}>
                    {() => (
                        <>
                            {expandableCards.map((card: SliceExpandableCardsCollectionCardView, index: number) => (
                                <SlidesItem
                                    key={index}
                                    className={cx({
                                        card_picture_bottom_container: !isTitleOverPicture,
                                        mg_slide_item: isTitleOverPicture,
                                        card_bordered: !isDark,
                                    })}
                                    onClick={() => {
                                        handleOpenModel(index);
                                    }}
                                >
                                    {slideContent(card)}
                                </SlidesItem>
                            ))}
                        </>
                    )}
                </Slides>
            )}

            {slideIndexModalContent !== null &&
                createPortal(
                    <ExpandableCardsCollectionModal
                        slices={expandableCards}
                        changeSlice={setSlideIndexModalContent}
                        index={slideIndexModalContent}
                        onClose={() => handleOpenModel(null)}
                    />,
                    document.body
                )}
        </div>
    );
};
